import { fetcher, useClientPortalAPI, useQueryParams } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  showToast,
} from "@certn/cascadia";
import { Container, RadioGroup } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useContext, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { PaymentCard } from "./PaymentCard";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { OrderContext } from "../../../pages/order/OrderContext";
import { useOrderBillingProfile } from "../../../pages/order/steps/useOrderBillingProfile";
import { AddPaymentProfileModal } from "../../../pages/payment-profiles/components/AddPaymentProfileModal";

interface Props {
  onClose: () => void;
}

export function PaymentProfileList(props: Props) {
  const { data: profile, mutate } = useOrderBillingProfile();
  const { client } = useClientAccount();
  const { onClose } = props;
  const paymentprofile_set =
    profile?.customer_account?.paymentprofile_set || [];
  // Paginated API total — embedded `paymentprofile_set.length` can be lower
  // than the real total (e.g. archived profiles), which false-succeeds polling
  // (DAT-1731 Bugbot).
  const { data: paymentProfilesPage, mutate: mutatePaymentProfiles } =
    useClientPortalAPI(
      client?.id
        ? `/api/internal/client-portal/customer-accounts/${client.id}/payment-profiles/`
        : null
    );
  const { clearParam, setParam } = useQueryParams();
  const [saving, setSaving] = useState(false);
  const { order } = useContext(OrderContext);
  const { t } = useTranslation();
  const [selected, setSelected] = useState(profile?.payment_profile?.id || "");
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on unmount / reschedule so post-timeout awaits do not run after leave.
  const closeWorkGenerationRef = useRef(0);

  const paymentProfileBaseline =
    typeof paymentProfilesPage?.count === "number"
      ? paymentProfilesPage.count
      : paymentProfilesPage?.results
        ? paymentProfilesPage.results.length
        : undefined;

  useEffect(() => {
    return () => {
      closeWorkGenerationRef.current += 1;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await fetcher.submit(
        `/api/internal/client-portal/order/${order.group}/set-payment-profile/`,
        { payment_profile_id: selected }
      );
      showToast(t("Payment profile updated"), { type: "success" });
      mutate();
    } catch (error) {
      showToast(t("Something went wrong, try again"), { type: "error" });
      console.error(error);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const handleAddNewPayment = () => {
    if (paymentProfileBaseline === undefined) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
      closeWorkGenerationRef.current += 1;
    }
    setShowNewPaymentModal(true);
    setParam("open-new-payment-profile-modal", "true");
  };

  const handleCloseModal = async () => {
    setShowNewPaymentModal(false);
    clearParam("open-new-payment-profile-modal");

    const generation = ++closeWorkGenerationRef.current;
    try {
      await fetcher.submit(
        `/api/internal/client-portal/order/${order.group}/set-default-payment-profile/`
      );
    } catch (error) {
      console.error(error);
    }
    if (generation !== closeWorkGenerationRef.current) return;
    try {
      await Promise.all([mutate(), mutatePaymentProfiles()]);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container className={styles.container}>
      <RadioGroup
        className={styles.radioGroup}
        isCard
        onChange={(value) => setSelected(value)}
        aria-label="Payment methods"
        value={selected}
      >
        {paymentprofile_set.map((profile) => (
          <PaymentCard profile={profile} key={profile.id} />
        ))}
      </RadioGroup>
      <Stack orientation="horizontal" justify="space-between" gap="space-4">
        <GhostButton
          size="medium"
          onPress={handleAddNewPayment}
          isDisabled={paymentProfileBaseline === undefined}
        >
          <Trans>Add new</Trans>
        </GhostButton>
        <Stack orientation="horizontal">
          <ButtonGroup>
            <BasicButton size="medium" onPress={onClose}>
              <Trans>Close</Trans>
            </BasicButton>
            <PrimaryButton
              size="medium"
              isDisabled={!selected || saving}
              onPress={onSave}
            >
              <Trans>Choose payment method</Trans>
            </PrimaryButton>
          </ButtonGroup>
        </Stack>
      </Stack>
      {showNewPaymentModal &&
        paymentProfileBaseline !== undefined &&
        client?.id && (
          <AddPaymentProfileModal
            customerAccountId={client.id}
            defaultAddress={{
              country: profile?.address.country || undefined,
              postal_code: profile?.address.postal_code || undefined,
            }}
            handleClose={handleCloseModal}
            initialPaymentProfileCount={paymentProfileBaseline}
          />
        )}
    </Container>
  );
}

const styles = {
  radio: css({
    flexDirection: "row",
    justifyContent: "flex-start",
  }),
  radioGroup: css({
    gap: "var(--space-4)",
  }),
  container: css({
    gap: "var(--space-4)",
    width: "unset",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    paddingBottom: "var(--space-0)",
  }),
};
